import { ChangeDetectorRef, NgZone, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { HumanDurationPipe } from '@app/modules/human-duration-pipe/pipes/human-duration.pipe';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/angular';

class TimeAgoPipeInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = TimeAgoPipeInputError.name;
  }
}

@Pipe({
    name: 'timeAgo',
    pure: false,
    standalone: true,
})
export class TimeAgoPipe implements PipeTransform, OnDestroy {
  private cdRef = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  private humanDurationPipe: HumanDurationPipe = new HumanDurationPipe();
  private currentTimer: number;
  private maxSignificantUnits = 1;
  private lastValue: DateTime;
  private lastText = '';

  ngOnDestroy(): void {
    this.removeTimer();
  }

  transform(value: DateTime | string | number, maxSignificantUnits?: number): string {
    if (!value) {
      return '';
    }

    let dateTime: DateTime;
    if (DateTime.isDateTime(value)) {
      dateTime = value;
    } else if (typeof value === 'string') {
      dateTime = DateTime.fromISO(value);
    } else if (typeof value === 'number') {
      dateTime = DateTime.fromMillis(value);
    } else {
      Sentry.captureException(new TimeAgoPipeInputError('Invalid input to TimeAgoPipe'),{
        extra: {
          value,
        },
      });
      return '';
    }

    if (!dateTime.isValid) {
      Sentry.captureException(new TimeAgoPipeInputError('Invalid DateTime input to TimeAgoPipe'), {
        extra: {
          invalidReason: dateTime.invalidReason,
          invalidExplanation: dateTime.invalidExplanation,
        }
      });
      return '';
    }

    if (this.hasChanged(dateTime)) {
      // console.log('changed');
      this.lastValue = dateTime;
      this.removeTimer();
      this.createTimer();

      if (maxSignificantUnits != null) {
        this.maxSignificantUnits = maxSignificantUnits;
      }

      const diff = DateTime.local().diff(dateTime);
      if (diff.valueOf() < 1000) {
        this.lastText = 'now';
      } else {
        this.lastText = `${this.humanDurationPipe.transform(
          diff.valueOf(),
          this.maxSignificantUnits,
        )} ago`;
      }
    } else {
      this.createTimer();
    }

    return this.lastText;
  }

  createTimer() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    if (this.currentTimer) {
      return;
    }
    const timeToUpdate = this.getMillisecondsUntilUpdate(this.lastValue);
    // console.log(timeToUpdate);
    this.currentTimer = this.ngZone.runOutsideAngular(() => {
      if (typeof window !== 'undefined') {
        return window.setTimeout(() => {
          // console.log('timed out');
          const diff = DateTime.local().diff(_this.lastValue).valueOf();
          if (diff < 1000) {
            _this.lastText = 'now';
          } else {
            _this.lastText = `${_this.humanDurationPipe.transform(
              diff,
              this.maxSignificantUnits,
            )} ago`;
          }
          _this.currentTimer = null;
          _this.ngZone.run(() => _this.cdRef.markForCheck());
        }, timeToUpdate);
      }
    });
  }

  removeTimer() {
    if (this.currentTimer) {
      window.clearTimeout(this.currentTimer);
      this.currentTimer = null;
    }
  }

  // TODO: Should this be based on time received instead of time measured?
  getMillisecondsUntilUpdate(value: DateTime): number {
    if (!DateTime.isDateTime(value) || !value.isValid) {
      // Should this be exponential back off?
      return 1000;
    }

    let elapsedMilliseconds = DateTime.local().diff(value).valueOf();

    // If the current elapsed time is less than one minute, return the number of seconds until the next full second plus 1 millisecond
    if (elapsedMilliseconds < 60000) {
      const millisecondsUntilNextSecond = (60000 - elapsedMilliseconds) % 1000;
      return millisecondsUntilNextSecond + 501;
    }

    // If the current elapsed time is one minute or more, return the number of seconds until the next full minute plus 1 millisecond
    if (elapsedMilliseconds > 3600000) {
      elapsedMilliseconds = elapsedMilliseconds % 3600000;
    }
    const millisecondsUntilNextMinute = (3600000 - elapsedMilliseconds) % 60000;
    return millisecondsUntilNextMinute + 501;
  }

  hasChanged(value: DateTime) {
    if (
      !DateTime.isDateTime(value) ||
      !value.isValid ||
      !DateTime.isDateTime(this.lastValue) ||
      !this.lastValue.isValid
    ) {
      return true;
    }
    return !value.equals(this.lastValue);
  }
}
